// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the bin, and the thirty days
//  ---------------------------------------------------------------------------
//  A thirty-day buffer was decided on 2026-08-20 and nothing was built. This
//  is the store half: what a deletion looks like once it has happened, how long
//  it has left, and how any ref finds the deletion it was swept up in. The
//  DOOR — deleting, restoring, sweeping — is Phase 4.
//
//  A DELETION IS ONE ENTRY, NOT ONE PER RECORD. Deleting a project takes its
//  containment subtree with it (md-contain.ts), and restore has to bring the
//  whole thing back together. One entry per record would leave restore guessing
//  what was deleted alongside what, from timestamps — which is how a restore
//  brings back four of a project's thirty-seven records and calls it done.
//
//  SO THE ENTRY IS KEYED ON THE ROOT AND NAMES ITS MEMBERS, each with the title
//  the placeholder will show and the document restore will write back. The
//  member list is what makes mdTrashOf possible: a desk card holding a ref has
//  no idea what the ref was deleted as part of, and it must not have to walk
//  every entry to find out.
//
//  THE MEMBER INDEX IS A CACHE, AND IT IS STAMPED. The sync applier writes these
//  keys directly — a pull can add or remove a deletion with no call into this
//  module — so an index that trusted itself would report a restored record as
//  still deleted until the tab was reloaded. It is rebuilt whenever the write
//  counter under the family moves, the same fingerprint stance md-record-index.ts
//  takes and for the same reason: a forgotten call site must cost a rebuild,
//  never correctness.
//
//  EXPIRY IS READ, NOT ENFORCED, HERE. An entry past its buffer answers
//  `mdTrashExpired` true and is invisible to `mdTrashOf`, so a countdown that
//  reached zero stops showing a placeholder even on a device that has not run a
//  sweep yet. Actually removing it — and releasing the bytes it was holding — is
//  Phase 4.5, because that is a WRITE and this is a read.
//
//  WALL-CLOCK IS FINE. Two devices disagreeing by a few minutes about whether
//  day thirty has passed costs nothing; the tombstone that keeps the deletion
//  true is permanent and is not computed from this.
// ════════════════════════════════════════════════════════════════════════════

/** One member of a deletion: what it was called, and what it was. */
interface MdTrashMember {
  ref: string;
  title?: string;
  /** The stored document, so restore writes back what was actually there. */
  doc?: unknown;
  /** Which storage document it came out of, when that is not derivable. */
  home?: string;
  /** Documents that were wholly this member's — a project's writing, a desk's
      legacy scene, a database and its rows. Key → the value that was there. */
  docs?: Record<string, unknown>;
  /** The bytes this member OWNS, so 4.5 knows what to let go of at expiry and
      4.3 knows what not to. Hashes, never sizes: the name is the checksum. */
  blobs?: string[];
  /** The day this record was sitting in, so its tombstone can be raised — and
      so restore knows which document to put the row back into. */
  day?: string;
}

/** One deletion. */
interface MdTrashEntry {
  v?: number;
  ref: string;
  deletedAt: number;
  members: MdTrashMember[];
}

(function (g: any) {
  'use strict';

  const MD_TRASH_DAYS = 30;
  const MD_TRASH_PREFIX = 'md:trash:';
  const MD_TRASH_VERSION = 1;
  const DAY_MS = 24 * 60 * 60 * 1000;

  const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
  /* A blob's name is the SHA-256 of its bytes, spelled the way md-blobs.ts
     spells it. Anything else in a `hash` field is not one of ours and must not
     reach mdBlobRelease, which would answer bad_ref for it anyway. */
  const HASH_RE = /^[0-9a-f]{64}$/;

  const refOk = (value: unknown): boolean =>
    typeof value === 'string' && !!value && !!(g.mdRecordRefParse && g.mdRecordRefParse(value));

  /* The dayless form, for the same reason md-parent.ts uses it: a record that
     moved days must not become a different key mid-deletion. */
  function flat(ref: unknown): string {
    const parsed = typeof ref === 'string' && g.mdRecordRefParse ? g.mdRecordRefParse(ref) : null;
    return parsed ? `${parsed.type}:${parsed.id}` : '';
  }

  function mdTrashKey(ref: unknown): string {
    const key = flat(ref);
    return key ? `${MD_TRASH_PREFIX}${key}` : '';
  }

  /** Whether a value is an entry this build will store. */
  function mdTrashEntryOk(value: unknown): boolean {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const e = value as MdTrashEntry;
    if (!refOk(e.ref)) return false;
    if (typeof e.deletedAt !== 'number' || !Number.isFinite(e.deletedAt)) return false;
    if (!Array.isArray(e.members) || !e.members.length) return false;
    return e.members.every((m) => !!m && typeof m === 'object' && refOk((m as MdTrashMember).ref));
  }

  function normalise(value: unknown): MdTrashEntry | null {
    if (!mdTrashEntryOk(value)) return null;
    const e = value as MdTrashEntry;
    return {
      v: MD_TRASH_VERSION,
      ref: flat(e.ref),
      deletedAt: e.deletedAt,
      members: e.members.map((m) => ({
        ref: flat(m.ref),
        ...(typeof m.title === 'string' ? { title: m.title } : {}),
        ...('doc' in m ? { doc: m.doc } : {}),
        ...(typeof m.home === 'string' ? { home: m.home } : {}),
        ...(m.docs && typeof m.docs === 'object' && !Array.isArray(m.docs)
          ? { docs: m.docs } : {}),
        ...(Array.isArray(m.blobs)
          ? { blobs: m.blobs.filter((h) => HASH_RE.test(String(h))) } : {}),
        ...(typeof m.day === 'string' && DAY_RE.test(m.day) ? { day: m.day } : {}),
      })),
    };
  }

  /* ── how long is left ─────────────────────────────────────────────────── */

  /** Whole days remaining, rounded UP: a person told "1 day left" has a day,
      and rounding it away takes hours off the promise. Never below zero. */
  function mdTrashDaysLeft(entry: unknown, now?: unknown): number {
    const at = entry && typeof entry === 'object'
      ? Number((entry as MdTrashEntry).deletedAt) : NaN;
    if (!Number.isFinite(at)) return 0;
    const clock = typeof now === 'number' && Number.isFinite(now) ? now : Date.now();
    const left = (at + MD_TRASH_DAYS * DAY_MS) - clock;
    return left <= 0 ? 0 : Math.ceil(left / DAY_MS);
  }

  function mdTrashExpired(entry: unknown, now?: unknown): boolean {
    return mdTrashDaysLeft(entry, now) <= 0;
  }

  /* ── the store ────────────────────────────────────────────────────────── */

  function mdTrashPut(entry: unknown): boolean {
    const clean = normalise(entry);
    if (!clean) return false;
    return !!(g.mdWrite && g.mdWrite(mdTrashKey(clean.ref), clean));
  }

  function mdTrashRead(ref: unknown): MdTrashEntry | null {
    const key = mdTrashKey(ref);
    if (!key || !g.mdRead) return null;
    return normalise(g.mdRead(key, null));
  }

  function mdTrashList(): MdTrashEntry[] {
    const keys = g.mdStorageKeys ? g.mdStorageKeys(MD_TRASH_PREFIX) : [];
    const out: MdTrashEntry[] = [];
    for (const key of keys) {
      const entry = normalise(g.mdRead(key, null));
      if (entry) out.push(entry);
    }
    return out.sort((a, b) => b.deletedAt - a.deletedAt);
  }

  function mdTrashRemove(ref: unknown): boolean {
    const key = mdTrashKey(ref);
    if (!key || !g.mdRead || g.mdRead(key, null) == null) return false;
    return !!(g.mdRemove && g.mdRemove(key));
  }

  /* ── coming back (0.4) ────────────────────────────────────────────────── */

  /** The stamp a restored record must carry.

      NOT `Date.now()`. md-merge.ts refuses any version at or before the
      deletion, so a device whose clock is behind the deletion — a phone back
      from a flight, a machine with the wrong year — would write a "restored"
      record that the very next merge deletes again, silently, for ever. The
      stamp is therefore the LATER of now and one millisecond past the deletion:
      honest when the clock is fine, and correct when it is not.

      The other half of the rule is what this does NOT do: it does not prune the
      tombstone. A tombstone is permanent (2.4), because a device offline for
      sixty days must not come back and undo every deletion made while it was
      away — and md-merge.ts:136 already makes the pruning unnecessary. */
  function mdTrashRestoreStamp(entry: unknown, now?: unknown): number {
    const clock = typeof now === 'number' && Number.isFinite(now) ? now : Date.now();
    const at = entry && typeof entry === 'object'
      ? Number((entry as MdTrashEntry).deletedAt) : NaN;
    if (!Number.isFinite(at)) return clock;
    return Math.max(clock, at + 1);
  }

  /** One record, re-stamped so it survives its own deletion. A copy: the caller
      may still need the original, and a restore that mutated what it was handed
      would leave the bin holding a record claiming to be newer than itself. */
  function mdTrashRestoreRecord(rec: unknown, entry: unknown, now?: unknown): any | null {
    if (!rec || typeof rec !== 'object' || Array.isArray(rec)) return null;
    return { ...(rec as any), updatedAt: mdTrashRestoreStamp(entry, now) };
  }

  /* ── the member index ─────────────────────────────────────────────────── */

  let cache: Map<string, { root: string; deletedAt: number; member: MdTrashMember }> | null = null;
  let stamp = -1;

  function fingerprint(): number {
    if (typeof g.mdStorageWrites !== 'function') return -1;
    /* The epoch moves when everything changed at once — hydration, or another
       tab replacing the area — so an index built before one must not be
       compared against counts taken after it. */
    const epoch = typeof g.mdStorageEpoch === 'function' ? g.mdStorageEpoch() : 0;
    return epoch * 1e9 + g.mdStorageWrites(MD_TRASH_PREFIX);
  }

  function index() {
    const now = fingerprint();
    if (cache && now === stamp && now >= 0) return cache;
    const map = new Map<string, { root: string; deletedAt: number; member: MdTrashMember }>();
    for (const entry of mdTrashList()) {
      for (const member of entry.members) {
        /* First writer wins, and the list is newest-first, so a ref swept twice
           reports the most recent deletion — which is the one still counting. */
        if (!map.has(member.ref)) {
          map.set(member.ref, { root: entry.ref, deletedAt: entry.deletedAt, member });
        }
      }
    }
    cache = map;
    stamp = now;
    return map;
  }

  /** The deletion this ref was swept up in, or null. Expired entries answer
      null: the countdown reached zero, so the placeholder stops even on a device
      that has not swept yet. */
  function mdTrashOf(ref: unknown, now?: unknown): {
    root: string; deletedAt: number; daysLeft: number; member: MdTrashMember;
  } | null {
    const key = flat(ref);
    if (!key) return null;
    const hit = index().get(key);
    if (!hit) return null;
    const daysLeft = mdTrashDaysLeft({ ref: hit.root, deletedAt: hit.deletedAt, members: [] }, now);
    if (daysLeft <= 0) return null;
    return { root: hit.root, deletedAt: hit.deletedAt, daysLeft, member: hit.member };
  }

  /* ── what a place that showed it draws (4.4) ──────────────────────────────
     A record is SHOWN in twenty places and LIVES in one. Delete it and the one
     place it lived is right; the twenty go silently blank, which tells nobody
     that the thing is one tap from coming back or that the tap has a deadline.

     PURE, AND THAT IS THE POINT. A desk holding forty cards asks this forty
     times per frame. It takes the answer the resolver already gave the caller
     (md-resolve.ts's third answer) and a clock, and reads no storage at all —
     so the countdown cannot become the reason a board is slow to draw.

     THE MOMENT IS THE FACT; the count is arithmetic on it. An answer that
     arrived without `daysLeft` — an older build, a caller that had the entry
     and not the reading — is still answerable, so the count is recomputed here
     rather than trusted.

     ZERO DAYS IS NOT A PLACEHOLDER. mdTrashDaysLeft rounds UP so that somebody
     told "1 day left" has a day; the corollary is that the moment day thirty is
     complete the answer is 0, and at 0 the view goes. */
  function mdTrashMark(resolved: unknown, now?: unknown): {
    state: 'live' | 'deleted' | 'gone';
    daysLeft: number; deletedAt: number; root: string; title: string;
    label: string; left: string; restore: string; sentence: string;
  } {
    const nothing = {
      state: 'gone' as const, daysLeft: 0, deletedAt: 0, root: '', title: '',
      label: '', left: '', restore: '', sentence: '',
    };
    if (!resolved || typeof resolved !== 'object' || Array.isArray(resolved)) return nothing;
    const r = resolved as Record<string, unknown>;
    const title = typeof r.title === 'string' ? r.title : '';
    if (!r.trashed) return { ...nothing, state: 'live' as const, title };

    const deletedAt = Number(r.deletedAt);
    if (!Number.isFinite(deletedAt)) return nothing;
    const daysLeft = mdTrashDaysLeft({ deletedAt }, now);
    if (daysLeft <= 0) return nothing;

    /* One day, in the singular. A count is the whole of what this says, so
       saying it wrong is the whole of what a person reads. */
    const days = `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
    return {
      state: 'deleted' as const,
      daysLeft,
      deletedAt,
      root: typeof r.trashRoot === 'string' ? r.trashRoot : '',
      title,
      /* Three parts and a sentence, deliberately. A surface that can offer a
         real <button> builds the line from the parts; a surface that cannot —
         a wikilink inside somebody's writing, which is their words and is left
         exactly as written — shows the sentence. Neither scolds: what happened,
         how long is left, and the way back. */
      label: 'Deleted',
      left: days,
      restore: 'Restore',
      sentence: `Deleted — ${days} to bring it back.`,
    };
  }

  /* ══ THE DOOR (4.3, 4.5, 4.7, 4.8) ═══════════════════════════════════════
     Everything above is the store and the reading. This is the part that moves
     something into it, brings it back out, and eventually lets it go.

     A DELETION IS A MOVE, AND NOT A CALL TO SEVEN DELETE FUNCTIONS. Every kind
     already has one — mdProjectDelete, mdBoardDelete, mdDatabaseDelete — and
     every one of them DESTROYS: a project's writing document is removed, a
     database's rows and forms go, a desk's scene is dropped from both stores.
     They are correct for what they were written to do and they have no inverse,
     so a restore built on them would be a RECONSTRUCTION from whatever the
     entry happened to remember. Instead this reads the documents, carries them
     into the entry, and takes them out of the lists that name them. Delete and
     restore are then the same operation in two directions, which is the only
     way "restore puts it back whole" is a property rather than a hope.

     WHERE A KIND KEEPS ITSELF is the one piece of another module's business
     this file has to know, and it is stated once in `whereIs` rather than
     guessed at per call. md-versions.ts spells out four keys for the same
     reason and wrote down the same defence: asking each module would make the
     answer depend on all of them being loaded, and a capture that silently did
     not happen is indistinguishable from one that had nothing to capture.

     ALL OR NONE, IN AN ORDER RATHER THAN A TRANSACTION. localStorage has no
     transaction. What it has is an order in which a partial failure cannot lose
     anything:

       1. the entry — which holds every document
       2. the tombstones — which make the deletion true across devices
       3. the removals — which make it visible here

     A refusal at 1 deletes nothing. A refusal at 3 leaves a tombstone standing
     over a record still present, which the next merge settles exactly as it
     settles every other deletion. The other order loses somebody's project.
     md-sync.js already sorts tombstones ahead of the days they cover, for this
     reason, in the same words: "a day applied without its tombstone is a
     resurrection".

     AND ON THE WIRE THEY ARE ONE COMMIT. mdSyncCommitGroup (md-sync.js) sends
     the whole group through /api/sync/commit, so no device can hold the
     deletion without the way back out of it. It is asked for and probed for: a
     device with no transport deletes anyway and the ordinary push carries it,
     because deleting offline is not an error. */

  /** Where one thing keeps itself: the list it is a row of, and the documents
      that are wholly its own. */
  interface MdTrashWhere {
    ref: string;
    type: string;
    id: string;
    index?: { key: string; field: string };
    own: string[];
    day?: string;
    /** A desk records its deletion in a map of its own rather than in a day's.
        Same shape, same merge, different home — see K_DESK_TOMBS. */
    deskTomb?: boolean;
    /** The app's structure rather than a user's. Never deleted. */
    builtin?: boolean;
  }

  const K_SWEPT = 'md:trashSweptAt';
  /* WHICH DESKS ARE GONE, AND WHEN. md-board.ts owns this map and this door has
     to know it for the same reason `whereIs` knows the rest: asking the module
     would make the answer depend on it being loaded, and a tombstone that
     silently did not get raised is indistinguishable from one that was not
     needed. It travels as `desk-tombstones`; both names are in WIRE below. */
  const K_DESK_TOMBS = 'md:deskTombstones';
  const DAY_DOC = /^md:(events|records|completed|tombstones):(\d{4}-\d{2}-\d{2})$/;
  /* Every key the database module owns: the document, its rows, and the CRDT
     state riding beside each row. */
  const DB_DOC = /^md:(?:database:[a-z0-9]{6,40}|(?:crdt:)?database-row:[a-z0-9]{6,40}:[a-z0-9]{6,40}|(?:view|form):[a-z0-9]{6,40})$/;

  /* The subset of server/sync.js's KEY_PATTERN this door can produce. MUST STAY
     IN STEP WITH IT — and it is a subset on purpose: ONE key the grammar does
     not know refuses the WHOLE grouped commit, so a craft (deliberately off the
     wire, md-manifest.ts) travelling in a deletion group would land the
     deletion nowhere at all. md-sync.js's MD_TOMBSTONE_KEY_RE keeps a copy of
     one clause for the same reason. */
  const WIRE = /^(?:projects|pursuits|boards|project-records|desk-tombstones)$|^(?:project|pursuit|board|database|view|form):[a-z0-9]{6,40}$|^(?:crdt:)?database-row:[a-z0-9]{6,40}:[a-z0-9]{6,40}$|^(?:events|records|tombstones):\d{4}-\d{2}-\d{2}$|^trash:[a-z][a-z0-9]{0,15}:[a-z0-9]{1,64}$/;

  const keysUnder = (prefix: string): string[] =>
    (typeof g.mdStorageKeys === 'function' ? g.mdStorageKeys(prefix) : []);

  const read = (key: string): unknown => (typeof g.mdRead === 'function' ? g.mdRead(key, null) : null);

  const clockOf = (opts: unknown): number => {
    const at = opts && typeof opts === 'object' ? (opts as { now?: unknown }).now : opts;
    return typeof at === 'number' && Number.isFinite(at) ? at : Date.now();
  };

  function whereIs(refKey: unknown): MdTrashWhere | null {
    const r = typeof g.mdRecordRefParse === 'function' ? g.mdRecordRefParse(refKey) : null;
    if (!r) return null;
    const type: string = r.type;
    const id: string = r.id;
    const where: MdTrashWhere = { ref: `${type}:${id}`, type, id, own: [] };

    /* The ten are code. A domain has no row to remove and no document to carry,
       and "delete Spirit" is not a sentence this app can mean. */
    if (type === 'domain') { where.builtin = true; return where; }

    if (type === 'project' || type === 'pursuit') {
      const row = type === 'project'
        ? (g.mdProjectById ? g.mdProjectById(id) : null)
        : (g.mdPursuitById ? g.mdPursuitById(id) : null);
      if (row && row.builtin) { where.builtin = true; return where; }
      where.index = { key: `md:${type}s`, field: 'id' };
      where.own = [`md:${type}:${id}`];
      return where;
    }

    if (type === 'board') {
      /* The legacy localStorage scene only. A desk's drawing lives in
         IndexedDB now, and it is deliberately LEFT THERE: nothing is uploaded
         again when the desk comes back (4.7), and the bytes are let go at
         expiry with everything else (4.5). */
      where.index = { key: 'md:boards', field: 'id' };
      where.own = [`md:board:${id}`];
      /* AND THE DESK LIST MERGES NOW, so taking the row out of it says nothing
         on its own. `boards` used to be one last-write-wins document, where a
         shortened list WAS the deletion; it unions row by row since the desk
         became whole, deliberately, because overwriting lost a desk every time
         two devices made one between syncs. Under a union an absence is not
         evidence — the first device that still holds the row hands it straight
         back, named and empty, a few moments after the card disappears.
         md-board.ts's own delete has raised this tombstone since that change
         and said why; this door is the one the catalogue actually presses. */
      where.deskTomb = true;
      return where;
    }

    if (type === 'database') {
      /* Views and forms ride in documents of their own since 1C, named by
         their OWN ids rather than under the database's — collected by reading
         each one's databaseId. Leaving them out of the move left them behind
         as seeds: a peer offering a stray view document was one of the hands
         that kept a deleted database coming back (staging, 2026-09-01). */
      const rides = (prefix: string): string[] => keysUnder(prefix)
        .filter((key) => { const doc = read(key) as any; return !!doc && doc.databaseId === id; });
      where.own = [`md:database:${id}`,
        ...keysUnder(`md:database-row:${id}:`),
        ...keysUnder(`md:crdt:database-row:${id}:`),
        ...rides('md:view:'), ...rides('md:form:')];
      return where;
    }

    if (type === 'craft' || type === 'manifest') {
      for (const key of ['md:crafts', 'md:manifests']) {
        const rows = read(key);
        if (Array.isArray(rows) && rows.some((e: any) => e && e.instance === id)) {
          where.index = { key, field: 'instance' };
          return where;
        }
      }
      return where;
    }

    /* A record, in one of its two homes — the project backlog, or a day. */
    const backlog = read('md:projectRecords');
    if (Array.isArray(backlog) && backlog.some((row: any) => row && row.uid === id)) {
      where.index = { key: 'md:projectRecords', field: 'uid' };
      return where;
    }
    for (const kind of ['records', 'events']) {
      for (const key of keysUnder(`md:${kind}:`)) {
        const day = key.slice(kind.length + 4);
        if (!DAY_RE.test(day)) continue;
        const rows = read(key);
        if (!Array.isArray(rows) || !rows.some((row: any) => row && row.uid === id)) continue;
        where.index = { key, field: 'uid' };
        where.day = day;
        return where;
      }
    }
    return where;
  }

  function rowIn(list: unknown, field: string, id: string): any | null {
    if (!Array.isArray(list)) return null;
    return list.find((row: any) => row && typeof row === 'object' && row[field] === id) || null;
  }

  function presentAt(where: MdTrashWhere): boolean {
    if (where.builtin) return false;
    if (where.index) return !!rowIn(read(where.index.key), where.index.field, where.id);
    return where.own.some((key) => read(key) != null);
  }

  /* Every blob this member owns, read out of the documents it owns.

     A WALK RATHER THAN A TABLE, because the alternative is a list of field
     paths that goes stale the first time a kind learns to carry a file — and a
     hash that is missed here is bytes nobody ever reclaims, silently. `hash`
     and `spill` are the two names this app gives a blob (md-file.ts,
     md-board.ts); anything else that happens to be sixty-four hex characters is
     not collected, because it was not named as one. */
  function hashesIn(value: unknown, into: Set<string>, depth = 0): void {
    if (depth > 12 || !value || typeof value !== 'object') return;
    if (Array.isArray(value)) {
      for (const item of value) hashesIn(item, into, depth + 1);
      return;
    }
    for (const [key, held] of Object.entries(value as Record<string, unknown>)) {
      if ((key === 'hash' || key === 'spill') && typeof held === 'string' && HASH_RE.test(held)) {
        into.add(held);
      } else if (held && typeof held === 'object') {
        hashesIn(held, into, depth + 1);
      }
    }
  }

  /** What this member is called, for the placeholder that will stand in for it.
      The resolver when it is loaded, the row's own words when it is not — a
      member with no title renders as an unnamed thing, which is a list nobody
      can read. */
  function titleOf(refKey: string, row: unknown): string {
    if (typeof g.mdRecordResolve === 'function') {
      const found = g.mdRecordResolve(refKey);
      if (found && typeof found.title === 'string' && found.title) return found.title;
    }
    const r = (row && typeof row === 'object' ? row : {}) as Record<string, unknown>;
    for (const field of ['name', 'title']) {
      const value = r[field];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return String(g.MD_RECORD_PLACEHOLDER || 'New Record');
  }

  /** One write the plan intends. `value` null means the document goes. */
  interface MdTrashWrite { key: string; value: unknown }

  /** The sync key a storage key travels under, or '' for one that never
      travels. Asks md-sync.js, which owns the handful of renames, and refuses
      anything the server's grammar would not accept. */
  function wireKey(storageKey: string): string {
    const key = typeof g.mdSyncDocKeyOf === 'function'
      ? g.mdSyncDocKeyOf(storageKey)
      : (storageKey.indexOf('md:') === 0 ? storageKey.slice(3) : '');
    return key && WIRE.test(key) ? key : '';
  }

  const emptied = (value: unknown): boolean =>
    (Array.isArray(value) && !value.length)
    || (!!value && typeof value === 'object' && !Array.isArray(value)
      && !Object.keys(value as object).length);

  /** The group, as documents the server would accept. An emptied document is a
      null — that is how this app spells "gone" — EXCEPT a tombstone, which is
      never nulled and never empty here anyway (2.4). */
  function wireGroup(writes: MdTrashWrite[]): MdTrashWrite[] {
    const out: MdTrashWrite[] = [];
    for (const write of writes) {
      const key = wireKey(write.key);
      if (!key) continue;
      const tomb = key.indexOf('tombstones:') === 0 || key === 'desk-tombstones';
      out.push({ key, value: (!tomb && emptied(write.value)) ? null : write.value });
    }
    return out;
  }

  /** Land one intended write. Day documents go through md-store.js's door so
      the CRDT beside them follows; everything else is an ordinary document. */
  function land(write: MdTrashWrite): boolean {
    const day = DAY_DOC.exec(write.key);
    if (day && typeof g.mdStoreWrite === 'function') {
      return !!g.mdStoreWrite(day[1], day[2], write.value == null ? [] : write.value);
    }
    /* A DATABASE HAS A CACHE IN FRONT OF IT, AND THE CACHE IS WHAT A PERSON
       SEES. md-database.ts answers every read from `canonicalByDatabase` and
       only drops an entry when the write went through one of its own doors.
       Removing the key from under it empties storage and changes nothing on
       screen: mdTrashDelete would return ok for a database still sitting there
       until the next reload, which is the worst of the three outcomes a delete
       can have.

       mdDatabaseApplyDoc is that door — the same one sync applies through, so
       it invalidates the cache and tells subscribers. It is keyed the way the
       wire is keyed, without the `md:` a storage key carries. */
    if (DB_DOC.test(write.key) && typeof g.mdDatabaseApplyDoc === 'function') {
      return !!g.mdDatabaseApplyDoc(write.key.slice(3), write.value == null ? null : write.value);
    }
    if (write.value == null) return read(write.key) == null ? true : !!(g.mdRemove && g.mdRemove(write.key));
    return !!(g.mdWrite && g.mdWrite(write.key, write.value));
  }

  /* True while this door is landing its own writes. md-database.ts refuses
     any write to a database whose Trash entry is live — trash outranks the
     wire — and this flag is the one exception: a restore IS the door writing
     a trashed database back, and it lands before the entry is lifted so that
     a crash mid-restore leaves the thirty-day copy rather than half a
     library. */
  let landingDepth = 0;
  const mdTrashBusy = (): boolean => landingDepth > 0;

  async function commit(docs: MdTrashWrite[]): Promise<boolean> {
    if (!docs.length || typeof g.mdSyncCommitGroup !== 'function') return false;
    try {
      const res = await g.mdSyncCommitGroup(docs);
      return !!(res && res.ok);
    } catch (err) { return false; }
  }

  /** Why a deletion or a restore was refused. Named, never numbered. */
  type MdTrashReason = 'malformed' | 'missing' | 'builtin' | 'expired' | 'storage_full';

  /** A named refusal, said as a sentence.

      The reasons are names rather than numbers so that a surface can answer in
      words; this is where the words live, once, instead of in each of the four
      places that press Delete. An unrecognised reason still gets a sentence —
      a refusal nobody worded is still a refusal a person has to be told about,
      and silence is the one answer that is always wrong. */
  function mdTrashSayRefusal(reason: unknown): string {
    if (reason === 'storage_full') return 'There was no room to move it to Trash. Free some space and try again.';
    if (reason === 'missing') return 'It is not there any more.';
    if (reason === 'builtin') return 'This one ships with ManDana and cannot be deleted.';
    if (reason === 'expired') return `Its ${MD_TRASH_DAYS} days have run out.`;
    return 'It could not be moved to Trash.';
  }

  /** What a deletion would do, without doing any of it.

      Exported because the confirmation has to say how far a delete reaches
      BEFORE it happens — "also deletes 4 projects and 37 records" (2.6) — and
      a count derived a second way is a count that will one day disagree with
      what actually goes. */
  function mdTrashPlan(ref: unknown, opts?: unknown): any {
    const now = clockOf(opts);
    const root = flat(ref);
    if (!root) return { ok: false, reason: 'malformed' as MdTrashReason };
    const rootWhere = whereIs(root);
    if (!rootWhere) return { ok: false, reason: 'malformed' as MdTrashReason };
    if (rootWhere.builtin) return { ok: false, reason: 'builtin' as MdTrashReason };
    if (!presentAt(rootWhere)) return { ok: false, reason: 'missing' as MdTrashReason };

    /* Root first, then everything the containment tree puts under it. Nothing
       LINKED, at any depth: a desk shows records that live elsewhere, and they
       live on — only the card dies, because a card is the desk's arrangement
       and not the record. */
    const subtree: string[] = typeof g.mdParentSubtree === 'function'
      ? g.mdParentSubtree(root) : [];
    const refs = subtree.length ? subtree : [root];

    const next = new Map<string, unknown>();
    const readNext = (key: string): unknown => (next.has(key) ? next.get(key) : read(key));
    const tombs: Record<string, Record<string, number>> = {};
    const deskTombs: Record<string, number> = {};
    const members: MdTrashMember[] = [];

    for (const memberRef of refs) {
      const where = whereIs(memberRef);
      if (!where || where.builtin) continue;
      const member: MdTrashMember = { ref: where.ref };
      const blobs = new Set<string>();
      let found = false;

      if (where.index) {
        const rows = readNext(where.index.key);
        const row = rowIn(rows, where.index.field, where.id);
        if (row) {
          found = true;
          member.home = where.index.key;
          member.doc = row;
          hashesIn(row, blobs);
          next.set(where.index.key,
            (rows as any[]).filter((one: any) => !one || one[where.index!.field] !== where.id));
          if (where.day) {
            member.day = where.day;
            /* Raised for the day, not for the record: `tombstones:<day>` is
               uid → when, and md-merge.ts reads it against every version of
               that record it ever sees again. */
            (tombs[where.day] || (tombs[where.day] = {}))[where.id] = now;
          }
          /* Raised only where the row was actually found, exactly like the
             day's above: a tombstone for a desk this device never held would
             be this device deciding something it has no evidence about. */
          if (where.deskTomb) deskTombs[where.id] = now;
        }
      }

      const docs: Record<string, unknown> = {};
      for (const key of where.own) {
        const held = readNext(key);
        if (held == null) continue;
        found = true;
        docs[key] = held;
        hashesIn(held, blobs);
        next.set(key, null);
      }
      if (Object.keys(docs).length) member.docs = docs;

      /* A ref the tree named but that no home holds is not a member. Filing it
         would put a placeholder on screen for something nobody deleted. */
      if (!found) continue;
      member.title = titleOf(where.ref, member.doc);
      if (blobs.size) member.blobs = [...blobs];
      members.push(member);
    }

    if (!members.length) return { ok: false, reason: 'missing' as MdTrashReason };

    const entry: MdTrashEntry = { v: MD_TRASH_VERSION, ref: root, deletedAt: now, members };

    /* Tombstones ahead of the removals, and that order is the whole of "all or
       none" — see the section header. */
    const writes: MdTrashWrite[] = [];
    for (const [day, raised] of Object.entries(tombs)) {
      const merged = typeof g.mdMergeTombstones === 'function'
        ? g.mdMergeTombstones(g.mdStoreRead ? g.mdStoreRead('tombstones', day) : {}, raised)
        : Object.assign({}, g.mdStoreRead ? g.mdStoreRead('tombstones', day) : {}, raised);
      writes.push({ key: `md:tombstones:${day}`, value: merged });
    }
    /* The desks' map is one document for the whole account rather than one per
       day, so it is folded rather than replaced — mdMergeTombstones, the same
       function md-board.ts and md-store.js use, because a tombstone that can be
       lost is a deletion that comes back on every device at once. */
    if (Object.keys(deskTombs).length) {
      const held = read(K_DESK_TOMBS);
      const was = held && typeof held === 'object' && !Array.isArray(held)
        ? held as Record<string, number> : {};
      writes.push({ key: K_DESK_TOMBS, value: typeof g.mdMergeTombstones === 'function'
        ? g.mdMergeTombstones(was, deskTombs) : Object.assign({}, was, deskTombs) });
    }
    for (const [key, value] of next) writes.push({ key, value });

    return {
      ok: true, root, deletedAt: now, entry, members, writes,
      tombstones: tombs, deskTombstones: deskTombs,
      commit: [{ key: `trash:${root}`, value: entry }, ...wireGroup(writes)],
    };
  }

  const refused = (): MdTrashReason => {
    const fault = typeof g.mdStorageHealth === 'function' ? g.mdStorageHealth().lastFault : null;
    return fault && fault.quota ? 'storage_full' : 'missing';
  };

  /** Move something and its containment subtree into the bin.

      A desk has one read before the local move: its current scene is in
      IndexedDB, and after removing the index row no later sweep can honestly
      rediscover which pictures it owned. Other kinds still reach the local
      writes without yielding; a caller must await a desk deletion, as every UI
      caller already does, because preserving its release ledger is part of the
      delete rather than wire bookkeeping after it. */
  async function mdTrashDelete(ref: unknown, opts?: unknown): Promise<any> {
    const plan = mdTrashPlan(ref, opts);
    if (!plan.ok) return plan;

    /* A current desk's bytes are in IndexedDB, outside the synchronous plan.
       Ask the board model before the entry becomes the thirty-day copy; after
       the row is removed there is no honest way to rediscover which pictures
       belonged to this desk rather than merely being linked onto it. */
    if (typeof g.mdBoardOwnedBlobs === 'function') {
      for (const member of plan.entry.members as MdTrashMember[]) {
        const parsed = g.mdRecordRefParse ? g.mdRecordRefParse(member.ref) : null;
        if (!parsed || parsed.type !== 'board') continue;
        let owned: unknown = [];
        try { owned = await g.mdBoardOwnedBlobs(parsed.id); } catch (err) { owned = []; }
        if (!Array.isArray(owned)) continue;
        const blobs = new Set([...(member.blobs || []), ...owned.filter((hash) => HASH_RE.test(String(hash)))]);
        if (blobs.size) member.blobs = [...blobs];
      }
    }

    // 1 · the entry, which holds every document. Nothing else has happened yet.
    if (!mdTrashPut(plan.entry)) return { ok: false, reason: refused() };
    // 2 · the tombstones, then 3 · the removals. Already in that order.
    for (const write of plan.writes) land(write);

    const committed = await commit(plan.commit);
    return { ok: true, root: plan.root, deletedAt: plan.deletedAt,
      members: plan.members.length, entry: plan.entry, committed };
  }

  /** The entry a ref belongs to, whether it names the root or a member.

      Expiry is NOT applied here: the difference between "there is no such
      deletion" and "its thirty days ran out" is the difference between two
      sentences a person needs to be able to tell apart. */
  function entryFor(ref: unknown): MdTrashEntry | null {
    const key = flat(ref);
    if (!key) return null;
    const own = mdTrashRead(key);
    if (own) return own;
    for (const entry of mdTrashList()) {
      if (entry.members.some((m) => m.ref === key)) return entry;
    }
    return null;
  }

  /** Put a deletion back, whole.

      A GENERATION NEWER THAN THE DELETION, never a resurrection (0.4). The
      tombstone is permanent — md-merge.ts refuses any version at or before it —
      so the returning rows are stamped past it, and the entry goes rather than
      the tombstone.

      ONLY WHAT CARRIES A `uid` IS RE-STAMPED. A stamp is meaningful exactly
      where a tombstone can outrank it, which is a record in a day or in the
      backlog. A project's index row has no such contest — the list is one
      last-write-wins document — and inventing a field on it would be this
      module writing into another's shape for no gain.

      NOTHING IS UPLOADED. The bytes were never released (4.5 does that, at
      expiry), so a restore inside the window touches no blob verb at all. */
  async function mdTrashRestore(ref: unknown, opts?: unknown): Promise<any> {
    const now = clockOf(opts);
    const entry = entryFor(ref);
    if (!entry) return { ok: false, reason: 'missing' as MdTrashReason };
    if (mdTrashExpired(entry, now)) return { ok: false, reason: 'expired' as MdTrashReason };

    const stamp = mdTrashRestoreStamp(entry, now);
    const next = new Map<string, unknown>();
    const readNext = (key: string): unknown => (next.has(key) ? next.get(key) : read(key));

    for (const member of entry.members) {
      for (const [key, value] of Object.entries(member.docs || {})) next.set(key, value);
      if (!member.home || member.doc == null) continue;
      const row: any = member.doc;
      /* A DESK IS RE-STAMPED TOO, and on the field its own list reads. Before
         the desk list merged, a restored row needed no stamp: putting it back
         in a last-write-wins document WAS the restore. Now md-board.ts drops
         any row whose `stampOf` — the later of `edited` and `at` — is at or
         before the tombstone, so a row returned with the stamp it was deleted
         with is deleted again by the very next merge, silently, for ever. Same
         rule as `updatedAt` above, same reason, different field. */
      const desk = !!row && typeof row === 'object' && typeof row.uid !== 'string'
        && typeof row.id === 'string' && member.home === 'md:boards';
      const back = (row && typeof row === 'object' && typeof row.uid === 'string')
        ? { ...row, updatedAt: stamp }
        : (desk ? { ...row, edited: stamp } : row);
      const held = readNext(member.home);
      const rows = Array.isArray(held) ? held : [];
      const id = row && typeof row === 'object'
        ? (row.uid ?? row.id ?? row.instance) : null;
      const field = row && typeof row === 'object'
        ? ('uid' in row ? 'uid' : ('id' in row ? 'id' : 'instance')) : 'id';
      next.set(member.home,
        [...rows.filter((one: any) => !one || one[field] !== id), back]);
    }

    const writes: MdTrashWrite[] = [];
    for (const [key, value] of next) writes.push({ key, value });
    landingDepth += 1;
    try { for (const write of writes) land(write); } finally { landingDepth -= 1; }
    mdTrashRemove(entry.ref);

    const committed = await commit([...wireGroup(writes),
      { key: `trash:${entry.ref}`, value: null }]);
    return { ok: true, root: entry.ref, members: entry.members.length, stamp, committed };
  }

  /* ── the buffer runs out (4.5) ────────────────────────────────────────────
     Four things go, and the tombstone is not one of them. The entry, the
     versions of every record it held, every line of their history — 4.1 left
     `md:history:` for exactly this, and nothing has swept those keys until now,
     which matters because the whole plan exists to relieve a ~5 MB wall — and
     ONLY NOW the blob refs.

     A REF RELEASED AT DELETION WOULD BREAK RESTORE. That is why nothing above
     touches mdBlobRelease and why this is the only place that does.

     WALL-CLOCK IS FINE. Whether day thirty has passed is a local convenience,
     not a rule two devices have to agree on: the entry is last-write-wins, and
     the tombstone that keeps the deletion true is not computed from it. */
  async function expire(entry: MdTrashEntry): Promise<any> {
    let versions = 0;
    let logs = 0;
    let kept = 0;
    let collected = 0;
    const released: string[] = [];
    for (const member of entry.members) {
      if (typeof g.mdHistoryLogRemoveFor === 'function') {
        logs += Number(g.mdHistoryLogRemoveFor(member.ref)) || 0;
      }
      if (typeof g.mdVersionRemoveFor === 'function') {
        try { versions += Number(await g.mdVersionRemoveFor(member.ref)) || 0; }
        catch (err) { /* the store already recorded it; a sweep never throws */ }
      }
      /* Named, not numbered, and named as the record that OWNS the bytes: after
         0.7 a hold is (account, hash, record), and a release that did not name
         one would take the object from every other record in the account. A
         build with no blob layer releases nothing and says so, rather than
         reporting bytes it never reclaimed. */
      if (typeof g.mdBlobRelease !== 'function') continue;
      for (const hash of (member.blobs || [])) {
        let res: any = null;
        const parsed = g.mdRecordRefParse ? g.mdRecordRefParse(member.ref) : null;
        /* Files are put as `record:<id>` whatever word renders that record;
           desks put as `board:<id>`. Releasing `note:<id>` looked precise and
           could never match the hold md-file.ts actually placed. */
        const owner = parsed && parsed.type === 'board'
          ? `board:${parsed.id}` : (parsed ? `record:${parsed.id}` : member.ref);
        try { res = await g.mdBlobRelease(hash, owner); } catch (err) { res = null; }
        if (res && res.ok) {
          released.push(hash);
          if (res.collected) collected += 1; else kept += 1;
        }
      }
    }
    mdTrashRemove(entry.ref);
    return { versions, logs, released, collected, kept };
  }

  /** Has this device swept today? A device-local convenience, kept outside
      every family so no producer offers it and no grammar accepts it. */
  function mdTrashSweepDue(now?: unknown): boolean {
    const clock = clockOf(now);
    const held = read(K_SWEPT) as { at?: unknown } | null;
    const at = held && typeof held === 'object' ? Number(held.at) : NaN;
    return !Number.isFinite(at) || (clock - at) >= DAY_MS;
  }

  /** Every entry past its buffer, gone. Answers what went, so a caller can say
      so rather than guess. */
  async function mdTrashSweep(opts?: unknown): Promise<any> {
    const now = clockOf(opts);
    const out = { swept: [] as string[], members: 0, versions: 0, logs: 0,
      released: [] as string[], collected: 0, kept: 0 };
    for (const entry of mdTrashList()) {
      if (!mdTrashExpired(entry, now)) continue;
      const gone = await expire(entry);
      out.swept.push(entry.ref);
      out.members += entry.members.length;
      out.versions += gone.versions;
      out.logs += gone.logs;
      out.released.push(...gone.released);
      out.collected += gone.collected;
      out.kept += gone.kept;
    }
    if (g.mdWrite) g.mdWrite(K_SWEPT, { at: now });
    /* A null on a trash key is a restore made elsewhere, and an expiry looks
       identical on the wire — which is right: both mean "this entry is no
       longer a thing you can act on". The tombstone is what keeps the DELETION
       true, and it is untouched. */
    await commit(out.swept.map((root) => ({ key: `trash:${root}`, value: null })));
    return out;
  }

  /** Delete now means now (4.8): one entry, expired whatever the clock says.

      The same sweep asked of one entry, deliberately — a second code path is
      how "delete now" ends up leaving the history behind. */
  async function mdTrashPurge(ref: unknown, opts?: unknown): Promise<any> {
    void opts;
    const entry = entryFor(ref);
    if (!entry) return { ok: false, reason: 'missing' as MdTrashReason };
    const gone = await expire(entry);
    const committed = await commit([{ key: `trash:${entry.ref}`, value: null }]);
    return { ok: true, root: entry.ref, members: entry.members.length,
      versions: gone.versions, logs: gone.logs, released: gone.released,
      collected: gone.collected, kept: gone.kept, committed };
  }

  /* ── the wire ─────────────────────────────────────────────────────────────
     A deletion is the one thing that MUST reach the other device. Everything
     else in this app can arrive late and still be right; a record deleted on
     the laptop and still sitting on the phone is the app disagreeing with
     itself about what exists.

     Four changes make a family, and these are three of them — the fourth is
     the grammar in server/sync.js, which refuses every key it does not name.
     Ordinary last-write-wins is the right merge here: any device may delete a
     record, so any device may write the entry, and two devices deleting the
     same thing agree about what happened. */

  const SYNC_PREFIX = 'trash:';

  /** Every entry this device holds, keyed as the server keys them.

      AND THE MOVE CATCHES UP HERE. A device can come to hold a live entry and
      the documents it took, both at once: it re-pushed them in the race the
      entry lost, or it held them before this rule existed — Marwan's own
      browser was in exactly that state when the 2026-09-01 deletion would not
      stick. No entry will ever "arrive" on such a device again, so the
      arrival handler cannot reach it; this pass, made on every sync anyway,
      is where it lets go. Only the database family: every other kind
      converges through its index row and tombstone. An expired entry's
      documents are long gone, so it costs a read and lands nothing. */
  function mdTrashSyncDocs(): Record<string, MdTrashEntry> {
    const out: Record<string, MdTrashEntry> = {};
    for (const entry of mdTrashList()) {
      out[`${SYNC_PREFIX}${entry.ref}`] = entry;
      for (const member of entry.members) {
        for (const dockey of Object.keys(member.docs || {})) {
          if (DB_DOC.test(dockey) && read(dockey) != null) land({ key: dockey, value: null });
        }
      }
    }
    return out;
  }

  /** An arriving entry, or undefined for one this build cannot use — which the
      pull loop reads as "leave local alone" rather than as a deletion. */
  function mdTrashSanitiseSyncDoc(key: unknown, value: unknown): MdTrashEntry | undefined {
    if (typeof key !== 'string' || !key.startsWith(SYNC_PREFIX)) return undefined;
    const clean = normalise(value);
    /* The key and the entry must name the same record. They are written
       together and could only disagree through a bug or a rewrite in flight,
       and filing somebody's deletion under another record's name would make it
       unrestorable from either. */
    if (!clean || `${SYNC_PREFIX}${clean.ref}` !== key) return undefined;
    return clean;
  }

  /** …and land it. A null is a RESTORE made on another device: the entry goes,
      and the record it named came back in the same commit.

      AN ENTRY IS A MOVE, NOT A NOTICE. Filing it and leaving the local copies
      standing is how a deleted database resurrected on staging (2026-09-01):
      this device's next push offered the documents straight back, at a newer
      revision than the deletion, and every device un-deleted it together. So
      an arriving entry takes the move itself — every database-family document
      it holds is removed here, now, without waiting for the group's nulls to
      be pulled. (Only the database family: every other kind converges through
      its index row and tombstone, which the same commit carries.)

      And the null takes the move back: while the entry stood, every pulled
      database document in the restore's group was refused — trash outranks
      the wire — so if the server paged the group with the null behind the
      documents, what this device declined is exactly what its own thirty-day
      copy holds. An entry lifted inside its window re-lands whatever is
      missing; an EXPIRED entry's null is the opposite kind of removal — its
      documents are meant to be gone — and re-lands nothing. */
  function mdTrashApplyDoc(key: unknown, value: unknown): boolean {
    if (typeof key !== 'string' || !key.startsWith(SYNC_PREFIX) || !g.mdWrite) return false;
    const ref = key.slice(SYNC_PREFIX.length);
    if (value == null) {
      const held = mdTrashRead(ref);
      const removed = mdTrashRemove(ref);
      if (held && !mdTrashExpired(held)) {
        for (const member of held.members) {
          for (const [dockey, doc] of Object.entries(member.docs || {})) {
            if (DB_DOC.test(dockey) && read(dockey) == null) land({ key: dockey, value: doc });
          }
        }
      }
      return removed;
    }
    const clean = mdTrashSanitiseSyncDoc(key, value);
    if (!clean || !mdTrashPut(clean)) return false;
    for (const member of clean.members) {
      for (const dockey of Object.keys(member.docs || {})) {
        if (DB_DOC.test(dockey) && read(dockey) != null) land({ key: dockey, value: null });
      }
    }
    return true;
  }

  /** Tests and account switching — module state, and a stale index is the one
      way this file can be wrong. */
  function mdTrashForget(): void {
    cache = null;
    stamp = -1;
  }

  Object.assign(g, {
    MD_TRASH_DAYS, MD_TRASH_PREFIX, MD_TRASH_VERSION,
    mdTrashKey, mdTrashEntryOk, mdTrashPut, mdTrashRead, mdTrashList, mdTrashRemove,
    mdTrashOf, mdTrashDaysLeft, mdTrashExpired, mdTrashForget,
    mdTrashRestoreStamp, mdTrashRestoreRecord, mdTrashMark,
    mdTrashPlan, mdTrashDelete, mdTrashRestore, mdTrashSayRefusal,
    mdTrashSweep, mdTrashSweepDue, mdTrashPurge, mdTrashBusy,
    mdTrashSyncDocs, mdTrashSanitiseSyncDoc, mdTrashApplyDoc,
  });

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = {
      MD_TRASH_DAYS, MD_TRASH_PREFIX, MD_TRASH_VERSION,
      mdTrashKey, mdTrashEntryOk, mdTrashPut, mdTrashRead, mdTrashList, mdTrashRemove,
      mdTrashOf, mdTrashDaysLeft, mdTrashExpired, mdTrashForget,
      mdTrashRestoreStamp, mdTrashRestoreRecord, mdTrashMark,
      mdTrashPlan, mdTrashDelete, mdTrashRestore, mdTrashSayRefusal,
      mdTrashSweep, mdTrashSweepDue, mdTrashPurge, mdTrashBusy,
      mdTrashSyncDocs, mdTrashSanitiseSyncDoc, mdTrashApplyDoc,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
